import classNames from 'classnames';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInput } from 'src/components/ui/FormInput/FormInput';
import { formRules, maxLengthRules } from 'src/constants/formConstants';
import { productHardware } from 'src/constants/productConstants';
import { addProduct, editProduct } from 'src/store/actions/productActions';
import { useErrorSelector } from 'src/utils/useLoadingSelector';

import classes from '../ProductDetails.module.scss';
import ProductDetailsFormSkeleton from './ProductDetailsFormSkeleton';

export interface ProductDetailsFormData {
  model: string;
  hardware: string;
  firmware: string;
  weight: string;
  dimensions: string;
  thermalOutput: string;
  notes: string;
  imageUrl: string;
  restricted: boolean;
}

export interface ProductDetailsFormProps {
  loading?: boolean;
  edit?: boolean;
}

const ProductDetailsForm: FC<ProductDetailsFormProps> = ({
  loading = false,
  edit = false,
}) => {
  const { t } = useTranslation();

  const createError = useErrorSelector<ProductDetailsFormData>({
    actionType: edit ? editProduct.type : addProduct.type,
  });

  const { control, watch } = useFormContext<ProductDetailsFormData>();
  const notes = watch('notes');

  const hardwareList = productHardware?.map((hardware, i) => (
    <option key={i} value={hardware}>
      {hardware}
    </option>
  ));

  return (
    <form noValidate className={classNames({ [classes.Loading]: loading })}>
      {!loading ? (
        <>
          <section>
            <FormInput<ProductDetailsFormData>
              control={control}
              name="model"
              placeholder={t('products:enter_model')}
              label={t('common:model')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.model}
              helperText={createError?.model}
            />
            <FormInput<ProductDetailsFormData>
              control={control}
              name="hardware"
              placeholder={t('common:enter_address')}
              label={t('products:hardware')}
              rules={formRules}
              error={!!createError?.hardware}
              helperText={createError?.hardware}
              inputProps={{
                select: true,
                children: (
                  <>
                    <option disabled value={''}>{`${t(
                      'common:choose'
                    )}...`}</option>
                    {hardwareList}
                  </>
                ),
              }}
            />
            <FormInput<ProductDetailsFormData>
              control={control}
              name="firmware"
              placeholder={t('products:firmware_example')}
              label={t('products:firmware')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.firmware}
              helperText={createError?.firmware}
            />
            <FormInput<ProductDetailsFormData>
              control={control}
              name="weight"
              placeholder={t('products:enter_weight')}
              label={t('products:weight_kg')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.weight}
              helperText={createError?.weight}
            />
            <FormInput<ProductDetailsFormData>
              control={control}
              name="dimensions"
              placeholder={t('products:dimensions_example')}
              label={t('products:dimensions_mm')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.dimensions}
              helperText={createError?.dimensions}
            />
            <FormInput<ProductDetailsFormData>
              control={control}
              name="thermalOutput"
              placeholder={t('products:thermal_output_example')}
              label={t('products:thermal_output_kw')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!createError?.thermalOutput}
              helperText={createError?.thermalOutput}
            />
          </section>
          <FormInput<ProductDetailsFormData>
            control={control}
            name="notes"
            placeholder={t('products:product_notes')}
            label={`${t('common:notes')} (${t(
              'common:optional'
            ).toLowerCase()})`}
            inputProps={{
              fullWidth: true,
              multiline: true,
              rows: 5,
              inputProps: { maxLength: 2500 },
            }}
            className={classes.Multiline}
            error={!!createError?.notes}
            helperText={`${notes?.length || 0}/2500`}
          />
        </>
      ) : (
        <ProductDetailsFormSkeleton />
      )}
    </form>
  );
};

export default ProductDetailsForm;
